import { createContext, useState, useEffect, useContext } from 'react'
import { jwtDecode } from 'jwt-decode'
import { useNavigate, useLocation } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import useBaseService from '../hooks/services/useBaseService'
import useLocalStorageCleaner from '../hooks/useLocalStorageCleaner'
import { updateCSSVariables } from '../utils/cssVariables'
import { entities } from '../constants/entities'
import {
  markMovementSessionExpired,
  preserveMovementDraftsForRecovery,
} from '../pages/Movements/Movement/utils/movementDraftStorage'

export const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider')
  }
  return context
}

// 🎯 NOVA FUNÇÃO: Mapeia o Cargo (Role) para o Módulo e a View do Dashboard
export const getDashboardConfig = (user) => {
  const role = (user?.role || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

  console.log('🎯 role normalizado:', JSON.stringify(role))

  // 1. Módulos próprios (sem view específica)
  if (role.includes('estoque') || role.includes('almoxarifado')) {
    return { module: 'almoxarifado', view: null }
  }
  // Gestor de Expedição vê o mesmo dashboard do Operador de Estoque de Almoxarifado
  if (role.includes('gestor') && role.includes('expedicao')) {
    return { module: 'almoxarifado', view: null }
  }
  if (role.includes('tecnico') || role.includes('manutencao')) {
    return { module: 'manutencao', view: null }
  }

  // 2. Módulo de Aluguel com Views específicas por setor
  if (role.includes('atendente') || role.includes('atendimento')) {
    return { module: 'aluguel', view: 'sac' }
  }
  if (role.includes('logistica')) {
    return { module: 'aluguel', view: 'logistica' }
  }
  if (role.includes('expedição') || role.includes('expedicao')) {
    return { module: 'aluguel', view: 'expedicao' }
  }
  if (
    role.includes('licitação') ||
    role.includes('licitacao') ||
    role.includes('contrato')
  ) {
    return { module: 'aluguel', view: 'licitacao' }
  }
  if (role.includes('financeiro') || role.includes('faturamento')) {
    return { module: 'aluguel', view: 'financeiro' }
  }
  if (
    role.includes('direção') ||
    role.includes('direcao') ||
    role.includes('admin') ||
    role.includes('super')
  ) {
    return { module: 'aluguel', view: 'direcao' }
  }

  // 3. Fallback padrão (cai no aluguel com visão geral)
  return { module: 'aluguel', view: 'padrao' }
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const { post: baseServicePost } = useBaseService(navigate)

  // Limpeza de localStorage específica (MANTIDO DO SEU CÓDIGO)
  const pathsToKeepShipmentData = [
    { pathPattern: '/aluguel/carregamentos/validar', idSegmentIndex: 2 },
    { pathPattern: '/aluguel/carregamentos/criar', idSegmentIndex: 2 },
    { pathPattern: '/aluguel/carregamentos/validar' },
  ]
  const shipmentKeyPrefixesToRemove = [
    'shipmentReceivedEquipments',
    'shipmentReceivedVolumes',
  ]
  useLocalStorageCleaner(pathsToKeepShipmentData, shipmentKeyPrefixesToRemove)

  const pathsToKeepMovementsData = [
    { pathPattern: '/aluguel/carregamentos/validar', idSegmentIndex: 2 },
    { pathPattern: '/aluguel/carregamentos/criar', idSegmentIndex: 2 },
    { pathPattern: '/aluguel/carregamentos/validar' },
  ]
  const movementKeyPrefixesToRemove = ['selectedMovementId']
  useLocalStorageCleaner(pathsToKeepMovementsData, movementKeyPrefixesToRemove)

  // Valida token existente ao carregar
  useEffect(() => {
    const token = localStorage.getItem('token')
    let storedUser = JSON.parse(localStorage.getItem('user'))

    if (token) {
      try {
        const decodedToken = jwtDecode(token)
        if (decodedToken.exp * 1000 < Date.now()) {
          logout('Sua sessão expirou. Por favor, faça login novamente.', {
            preserveMovementDraft: true,
          })
        } else {
          // Garante que o role esteja no usuário salvo, mesmo em sessões antigas
          if (storedUser && !storedUser.role && decodedToken.role) {
            storedUser.role = decodedToken.role
            localStorage.setItem('user', JSON.stringify(storedUser))
          }
          setIsAuthenticated(true)
          setUser(storedUser)
        }
      } catch (error) {
        logout('Sua sessão expirou. Por favor, faça login novamente.', {
          preserveMovementDraft: true,
        })
      }
    }
    setLoading(false)
  }, [])

  // ⬇️ LOGIN ATUALIZADO: Agora captura o 'role' do token
  const login = (token) => {
    const decoded = jwtDecode(token)

    const userData = {
      id: decoded.sub, // sub é o padrão JWT para user ID
      username: decoded.username || '',
      email: decoded.email || '',
      role: decoded.role || 'user',
      dashboardModule: decoded.dashboard_module || 'aluguel',
      dashboardView: decoded.dashboard_view,
    }

    localStorage.setItem('token', token)
    localStorage.setItem('user', JSON.stringify(userData))
    setIsAuthenticated(true)
    setUser(userData)
  }

  const logout = async (message, { preserveMovementDraft = false } = {}) => {
    const preserveCurrentMovementDraft =
      preserveMovementDraft || location.pathname === '/aluguel/movimentos/criar'
    // 1. Verificamos se há um relatório rodando e cancelamos antes de limpar o token
    const storedExportId = localStorage.getItem('report_exportId')
    if (storedExportId) {
      try {
        await baseServicePost(
          entities.reports.cancel(storedExportId),
          {},
          { skipNotification: true }
        )
        // * Limpa a lista de confirmar devoluções
        localStorage.removeItem('returnConfirmations')
      } catch (e) {
        console.error('Erro ao cancelar relatório ao deslogar', e)
      }
    }

    if (preserveCurrentMovementDraft) {
      preserveMovementDraftsForRecovery()
      sessionStorage.clear()
      markMovementSessionExpired()
    } else {
      localStorage.clear()
      sessionStorage.clear()
    }
    setIsAuthenticated(false)
    setUser(null)
    updateCSSVariables('#4da8ff')
    navigate('/login', {
      state: { message: message || 'Você foi desconectado.' },
    })
  }

  if (loading) {
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <CircularProgress />
      </div>
    )
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
        loading,
        getDashboardConfig, // ⬅️ NOVO: Expõe a função para o Router usar
      }}>
      {children}
    </AuthContext.Provider>
  )
}
